import { AdPlatformKey, Region } from '@kaizenplatform/hugot-entity';
import { createPath } from 'history';
import { parse } from 'qs';
import { ClassAttributes, createElement, ReactElement, useContext } from 'react';
import { Redirect } from 'react-router';
import { compose, StateHandler, withState } from 'recompose';

import {
  CreationForShowFragment,
  OrganizationDetailFragment,
  ProjectDetailsForShowFragment,
} from '../../../../generated/graphql';
import { SingleAssignmentFragment, SingleOfferFragment } from '../../../../generated/graphql_offer';
import { getLatestDeliverable } from '../../../../graphql/creation';
import mergeUrlQuery from '../../../../utility/mergeUrlQuery';
import CreationsList from '../../../component/compound/CreationsList';
import EmptyState from '../../../component/compound/CreationsList/EmptyState';
import { Props as ItemProps } from '../../../component/compound/CreationsList/Item';
import CreativeSendingAdPlatformSelectorDialog from '../../../component/compound/CreativeSendingAdPlatformSelectorDialog';
import CreativeSendingToAmazonDialog from '../../../component/compound/CreativeSendingToAmazonDialog';
import CreativeSendingToFacebookDialog from '../../../component/compound/CreativeSendingToFacebookDialog';
import CreativeSendingToTiktokDialog from '../../../component/compound/CreativeSendingToTiktokDialog';
import CreativeSendingToYahooDialog from '../../../component/compound/CreativeSendingToYahooDialog';
import CreativeSendingToYoutubeDialog from '../../../component/compound/CreativeSendingToYoutubeDialog';
import DummyCreationsListItem from '../../../component/compound/DummyCreationsListItem';
import ProjectDeliverablesActions from '../../../component/compound/ProjectDeliverablesActions';
import { context as dependencyContext } from '../../../context/dependency';
import { context as historyContext } from '../../../context/history';
import { context as stateContext } from '../../../context/state';
import AssignmentList from './AssignmentList';

type Props = ClassAttributes<HTMLElement> & {
  project: ProjectDetailsForShowFragment | null;
  offer: SingleOfferFragment | null;
  reloadOffer: () => Promise<unknown>;
  className?: string;
};

type StateAndStateHandler = {
  selectedCreationId: string;
  setSelectedCreationId: StateHandler<string>;
  selectedSendingAdPlatform: AdPlatformKey | null;
  setSelectedSendingAdPlatform: StateHandler<AdPlatformKey>;
};

const ProjectDeliverables = compose<Props & StateAndStateHandler, Props>(
  withState('selectedCreationId', 'setSelectedCreationId', ''),
  withState('selectedSendingAdPlatform', 'setSelectedSendingAdPlatform', null),
)(
  ({
    project,
    offer,
    reloadOffer,
    className,
    selectedCreationId,
    setSelectedCreationId,
    selectedSendingAdPlatform,
    setSelectedSendingAdPlatform,
  }) => {
    const dependency = useContext(dependencyContext);
    const state = useContext(stateContext);
    const history = useContext(historyContext);

    const query = parse(state.location.search.slice(1));
    const organizationId = query.org;
    let organization: OrganizationDetailFragment;

    const region: Region =
      state.lastAccessedOrganization != null && state.lastAccessedOrganization.businessRegion.key
        ? state.lastAccessedOrganization.businessRegion.key
        : 'jp';

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }

    // クエパラにorganizationIdが存在しない場合はリダイレクトする
    if (!organizationId && organization) {
      return <Redirect to={createPath(mergeUrlQuery(history.location, { org: organization.id }))} />;
    }

    const fetchFacebookAdAccounts = dependency.sendCreativeToFacebookUsecase.fetchAdAccounts;

    const fetchAmazonAdAccounts = dependency.integrateAmazonAdAccountUsecase.fetchIntegratedAmazonAdAccounts;

    const fetchYahooAdAccounts = dependency.integrateYahooAdAccountUsecase.fetchIntegratedYahooAdAccounts;

    const fetchTiktokAdAccounts = dependency.integrateTiktokAdAccountUsecase.fetchIntegratedTiktokAdAccounts;

    const cancelCreativeSending = () => {
      setSelectedSendingAdPlatform(null);
      setSelectedCreationId('');
    };

    const uploadedAdPlatformKeys = state.creativeSending.uploadedAdPlatformKeys;

    if (!project) {
      return (
        <div className={className}>
          <CreationsList proposal={false}>
            {[...new Array(4)].map((_, i) => (<DummyCreationsListItem key={i} />) as ReactElement<ItemProps>)}
          </CreationsList>
        </div>
      );
    }

    const creations = project.creationSet!.creations.filter((creation) => creation.deliverables.length > 0);

    if (!offer || creations.length == 0) {
      return <EmptyState proposal={false} className={className} />;
    }

    const visibleCreations = creations.filter((creation) => {
      return creation.state !== 'canceled' && creation.state !== 'in_proposal' && creation.deliverables.length > 0;
    });
    const visibleCreationsByAssignment: {
      assignment: SingleAssignmentFragment;
      creations: CreationForShowFragment[];
    }[] = [];
    visibleCreations.forEach((c) => {
      const assignment = offer.assignments.find((a) => a.id == c.assignmentId) as SingleAssignmentFragment;
      const vcba = visibleCreationsByAssignment.find((cba) => cba.assignment.id == c.assignmentId);
      if (vcba) {
        vcba.creations.push(c);
      } else {
        visibleCreationsByAssignment.push({
          assignment: assignment,
          creations: [c],
        });
      }
    });

    if (visibleCreationsByAssignment.length == 0) {
      return <EmptyState proposal={false} className={className} />;
    }

    return (
      <div className={className}>
        <ProjectDeliverablesActions
          project={project}
          offer={offer}
          downloadAssets={async (params: any) => await dependency.showProjectUsecase.downloadAssets(params)}
        />

        <AssignmentList
          project={project}
          visibleCreationsByAssignment={visibleCreationsByAssignment}
          setSelectedCreationId={setSelectedCreationId}
          reload={reloadOffer}
        />

        {creations
          .filter((creation) => creation.deliverables.length > 0)
          .map((creation) => {
            const deliverable = getLatestDeliverable(creation)!;
            return [
              <CreativeSendingAdPlatformSelectorDialog
                creative={deliverable.creative}
                region={region}
                uploadedAdPlatforms={uploadedAdPlatformKeys.get(deliverable.id)}
                open={selectedCreationId === creation.id && !selectedSendingAdPlatform}
                onRequestSelectingNetwork={async (_, adPlatform) => {
                  if (adPlatform === 'facebook') {
                    const isReady = await fetchFacebookAdAccounts();
                    if (isReady) {
                      setSelectedSendingAdPlatform(adPlatform);
                      return;
                    }
                  } else if (adPlatform === 'google') {
                    setSelectedSendingAdPlatform(adPlatform);
                    return;
                  } else if (adPlatform === 'amazon') {
                    const isReady = await fetchAmazonAdAccounts();
                    if (isReady) {
                      setSelectedSendingAdPlatform(adPlatform);
                      return;
                    }
                  } else if (adPlatform === 'yahoo') {
                    const isReady = await fetchYahooAdAccounts();
                    if (isReady) {
                      setSelectedSendingAdPlatform(adPlatform);
                      return;
                    }
                  } else if (adPlatform === 'tiktok') {
                    const isReady = await fetchTiktokAdAccounts();
                    if (isReady) {
                      setSelectedSendingAdPlatform(adPlatform);
                      return;
                    }
                  }
                  cancelCreativeSending();
                }}
                onRequestClose={() => cancelCreativeSending()}
                isUploadedToAmazon={state.amazonUploadedCreative}
                isUploadedToYahoo={state.yahooUploadedCreative}
                isUploadedToTiktok={state.tiktokUploadedCreative}
                key={`networkSelectorDialog_${creation.id}`}
              />,
              <CreativeSendingToFacebookDialog
                open={selectedCreationId === creation.id && selectedSendingAdPlatform === 'facebook'}
                onRequestSendingCreative={(_, adAccount) =>
                  dependency.sendCreativeToFacebookUsecase.sendCreative(deliverable, adAccount)
                }
                onRequestClose={() => cancelCreativeSending()}
                key={`sendingToFacebookDialog_${creation.id}`}
              />,
              <CreativeSendingToYoutubeDialog
                open={selectedCreationId === creation.id && selectedSendingAdPlatform === 'google'}
                onRequestSendingCreative={(_, channel) =>
                  dependency.sendCreativeToYoutubeUsecase.sendCreative(deliverable, channel)
                }
                onRequestAddYoutubeChannel={() => dependency.sendCreativeToYoutubeUsecase.addConnectedChannel()}
                onRequestClose={() => cancelCreativeSending()}
                key={`sendingToYoutubeDialog_${creation.id}`}
              />,
              <CreativeSendingToAmazonDialog
                open={selectedCreationId === creation.id && selectedSendingAdPlatform === 'amazon'}
                onRequestSendingCreative={(_, adAccount) =>
                  dependency.integrateAmazonAdAccountUsecase.sendCreative(deliverable, adAccount, project.id)
                }
                onRequestClose={() => cancelCreativeSending()}
                key={`sendingToAmazonDialog_${creation.id}`}
              />,
              <CreativeSendingToYahooDialog
                open={selectedCreationId === creation.id && selectedSendingAdPlatform === 'yahoo'}
                onRequestSendingCreative={(_, adAccount) =>
                  dependency.integrateYahooAdAccountUsecase.sendCreative(deliverable, adAccount, project.id)
                }
                onRequestClose={() => cancelCreativeSending()}
                key={`sendingToYahoo_${deliverable.id}`}
              />,
              <CreativeSendingToTiktokDialog
                open={selectedCreationId === creation.id && selectedSendingAdPlatform === 'tiktok'}
                onRequestSendingCreative={(_, adAccount) =>
                  dependency.integrateTiktokAdAccountUsecase.sendCreative(deliverable, adAccount, project.id)
                }
                onRequestClose={() => cancelCreativeSending()}
                key={`sendingToTiktok_${deliverable.id}`}
              />,
            ];
          })}
      </div>
    );
  },
);

export default ProjectDeliverables;
